import { useUnenrolStudent } from '@/hooks/useUnenrolStudent';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import { LearnUponInstance } from '@/types/views/enrollment.types';
import { ConfirmationDialog } from 'klayowebshared';
import { CourseStudentRow } from '../table/CourseStudentsTable';

interface ConfirmUnenrolStudentDialogProps {
  row: CourseStudentRow;
  courseName?: string;
  theme?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ConfirmUnenrolStudentDialog = ({
  row,
  courseName,
  theme,
  onClose,
  onSuccess,
}: ConfirmUnenrolStudentDialogProps) => {
  const { translate } = useLocale();
  const { showSnackbar } = useSnackbar();

  const { mutate: unenrol, isPending } = useUnenrolStudent({
    onSuccess: () => {
      onClose();
      onSuccess?.();
    },
  });

  const handleAccept = () => {
    // Row must carry the LU portal — without it the backend cannot route the unenrol call.
    // This happens when the row originates from a code path that hasn't been updated to
    // surface learnUponInstance; fail loudly rather than silently picking the wrong portal.
    if (!row.learnUponInstance) {
      showSnackbar(translate('common.error'), 'error');
      return;
    }
    unenrol({
      learnUponEnrollmentId: row.learnUponEnrollmentId!,
      instance: row.learnUponInstance as LearnUponInstance,
      integrationId: row.integrationId ?? undefined,
    });
  };

  return (
    <ConfirmationDialog
      title={translate('manage.courseStudents.unenrolConfirmTitle')}
      question={translate('manage.courseStudents.unenrolConfirmQuestion', {
        studentName: row.name,
        courseName: courseName ?? '',
      })}
      cancelButton={translate('common.cancel')}
      acceptButton={translate('common.confirm')}
      theme={theme}
      loading={isPending}
      onCancel={onClose}
      onAccept={handleAccept}
    />
  );
};
